// src/api/api.ts

import { toast } from "@100mslive/hms-ai-ui";
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";

import { currentUser } from "../../src/utils/user";
import { getIPFromURL } from "../utils/utils";

/**
 * Type definition for the toast function.
 * Adjust the variant types as per your shadcn UI library's specifications.
 */
type ToastFunction = (options: {
  variant: "default" | "destructive" | null | undefined;
  title: string;
  description: string;
}) => void;

/**
 * Service names.
 * Extend this union type as you add more services.
 */
type ServiceName = "dashboard" | "dashboard2" | "base";

/**
 * Service URLs.
 * Each service name maps to its corresponding base URL from environment variables.
 */
const services: Record<ServiceName, string | undefined> = {
  dashboard:
    window?._env_?.REACT_APP_BACKEND_ENDPOINT ||
    process.env.REACT_APP_BACKEND_ENDPOINT,
  dashboard2:
    window?._env_?.REACT_APP_BACKEND2_ENDPOINT ||
    process.env.REACT_APP_BACKEND2_ENDPOINT,
  base: window?._env_?.REACT_APP_API_SERVER || process.env.REACT_APP_API_SERVER,
};

/**
 * Utility function to join base URL with endpoint URL.
 * @param baseUrl - The base URL of the service.
 * @param url - The endpoint URL.
 * @returns The combined full URL.
 */
export const joinUrl = (baseUrl: string, url: string): string => {
  return url.includes("://") ? url : `${baseUrl}${url}`;
};

/**
 * Interface defining the available HTTP methods with TypeScript generics.
 */
interface HttpMethods {
  get<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  head<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  post<T = any>(
    url: string,
    data?: any,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  put<T = any>(
    url: string,
    data?: any,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  patch<T = any>(
    url: string,
    data?: any,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  delete<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  options<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
  instance(): AxiosInstance;
  methods(): HttpMethods;
  cancellable(callback: (controller: AbortController) => void): HttpMethods;
  cancelRequest(): void;
  controller(): AbortController;
  addInterceptors(interceptors: Interceptor[]): void;
  url(url?: string): string;
}

/**
 * Type definition for Axios interceptors.
 */
type Interceptor = [
  (value: AxiosResponse) => AxiosResponse,
  (error: AxiosError) => Promise<never>,
];

/**
 * ToastService to manage the toast function globally.
 * This singleton allows Axios interceptors to trigger toasts without passing the toast function down.
 */
class ToastServiceClass {
  private toastFunction?: ToastFunction;

  /**
   * Registers the toast function.
   * This should be called inside a React component using useEffect or similar.
   * @param toast - The toast function from shadcn's useToast.
   */
  register(toast: ToastFunction) {
    this.toastFunction = toast;
  }

  /**
   * Shows a toast if the toast function is registered.
   * @param options - The toast options.
   */
  showToast(options: {
    variant: "default" | "destructive" | null | undefined;
    title: string;
    description: string;
  }) {
    if (this.toastFunction) {
      this.toastFunction(options);
    } else {
      console.warn("Toast function is not registered.");
    }
  }
}

// Instantiate the ToastService as a singleton.
const ToastService = new ToastServiceClass();

/**
 * Creates an authentication interceptor.
 * This interceptor listens for `401 Unauthorized` responses and triggers a toast notification.
 * @returns An Axios interceptor tuple.
 */
const createAuthInterceptor = (): Interceptor => [
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<never> => {
    const status = error.response?.status;
    if (status === 401) {
      if (currentUser()) {
        toast({
          variant: "destructive",
          title: "Session Expired",
          description: "Please login again.",
          className: "bg-bg-primary",
        });
        window.location.href = "/logout";
      }
      return new Promise<never>(() => {});
    }

    if (status === 403) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You do not have required permission.",
        className: "bg-bg-primary",
      });
      return new Promise<never>(() => {});
    }

    return Promise.reject(error);
  },
];

const createLog404Interceptor = (): Interceptor => [
  (response: AxiosResponse): AxiosResponse => response,

  async (error: AxiosError): Promise<never> => {
    const status = error.response?.status;
    if (status === 404) {
      const url = error.response?.config?.baseURL;

      if (url) {
        try {
          const ips = await getIPFromURL(url);
          if (ips && ips.length > 0) {
            console.warn(
              `404 Not Found: Server IPs - ${ips.join(", ")}, URL - ${url}`,
            );
          } else {
            console.warn(
              `404 Not Found: Unable to resolve server IP, URL - ${url}`,
            );
          }
        } catch (error) {
          console.warn(
            `404 Not Found: IP resolution failed, URL - ${url} - ${error}`,
          );
        }
      } else {
        console.warn(`404 Not Found: No URL available`);
      }
    }

    return Promise.reject(error);
  },
];

/**
 * Retrieves the access token from the current user.
 * @returns The access token string or undefined if not available.
 */
export const getAccessToken = (): string | undefined => {
  const user = currentUser();
  return user?.access_token;
};

/**
 * Constructs the headers for Axios requests based on the service name.
 * @param serviceName - The name of the service.
 * @returns An object containing the appropriate headers.
 */
export const getHeaders = (
  serviceName: ServiceName,
): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (["dashboard", "dashboard2", "base"].includes(serviceName)) {
    const accessToken = getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
  }
  return headers;
};

/**
 * Creates an Axios service with type-safe methods.
 * @param name - The name of the service ("dashboard" | "base").
 * @param options - Optional Axios request configuration.
 * @returns An object containing HTTP methods and utility functions.
 */
export const service = (
  name: ServiceName,
  options: AxiosRequestConfig = {},
): HttpMethods => {
  const baseURL = services[name];

  if (!baseURL) {
    throw new Error(`Base URL for service "${name}" is not defined.`);
  }

  const controller = new AbortController();
  const signal = controller.signal;

  const instance = axios.create({
    baseURL,
    headers: { ...getHeaders(name), ...(options.headers || {}) },
    signal,
    timeout: 300000,
    responseType: "json",
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });

  // Add authentication interceptor
  const authInterceptor = createAuthInterceptor();
  instance.interceptors.response.use(...authInterceptor);

  // Add 404 logging interceptor
  const log404Interceptor = createLog404Interceptor();
  instance.interceptors.response.use(...log404Interceptor);

  const methods: HttpMethods = {
    async head<T = any>(
      url: string,
      params?: Record<string, any>,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.head<T>(url, {
        params,
        responseType: "json",
        ...requestOptions,
      });
    },
    async get<T = any>(
      url: string,
      params?: Record<string, any>,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.get<T>(url, {
        params,
        responseType: "json",
        ...requestOptions,
      });
    },

    async post<T = any>(
      url: string,
      data?: any,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.post<T>(url, data, {
        responseType: "json",
        ...requestOptions,
      });
    },

    async put<T = any>(
      url: string,
      data?: any,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.put<T>(url, data, {
        responseType: "json",
        ...requestOptions,
      });
    },

    async patch<T = any>(
      url: string,
      data?: any,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.patch<T>(url, data, {
        responseType: "json",
        ...requestOptions,
      });
    },

    async delete<T = any>(
      url: string,
      params?: Record<string, any>,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.delete<T>(url, {
        params,
        responseType: "json",
        ...requestOptions,
      });
    },

    async options<T = any>(
      url: string,
      params?: Record<string, any>,
      requestOptions: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
      return instance.options<T>(url, {
        params,
        responseType: "json",
        ...requestOptions,
      });
    },

    instance(): AxiosInstance {
      return instance;
    },

    methods(): HttpMethods {
      return this;
    },

    cancellable(callback: (controller: AbortController) => void): HttpMethods {
      callback(controller);
      return this;
    },

    cancelRequest(): void {
      controller.abort();
    },

    controller(): AbortController {
      return controller;
    },

    addInterceptors(interceptors: Interceptor[]): void {
      interceptors.forEach(interceptor => {
        instance.interceptors.response.use(...interceptor);
      });
    },

    url(url: string = ""): string {
      return joinUrl(baseURL as string, url);
    },
  };

  return methods;
};

/**
 * The exported API object containing the service function and ToastService.
 */
const api = { service, ToastService };

export default api;
